<template>
  <div class="min-h-screen bg-gray-50 p-6 dark:bg-gray-900">
    <div class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">租户月结管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          按月份查看待结租户、预览月结明细，并确认线下打款结果。
        </p>
      </div>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div class="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700 dark:border-indigo-900/60 dark:bg-indigo-950/30 dark:text-indigo-200">
          规则：每月 8 号后可结算上个自然月金额，当前最多可结算到 {{ allowedSettlementMonth }}
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          :disabled="pageLoading"
          @click="refreshAll"
        >
          <ArrowPathIcon :class="pageLoading ? 'animate-spin' : ''" class="h-4 w-4" />
          刷新数据
        </button>
      </div>
    </div>

    <section class="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p class="text-sm font-medium text-gray-500 dark:text-gray-400">结算月份</p>
          <div class="mt-2 flex items-center gap-3">
            <input
              v-model="selectedMonth"
              type="month"
              class="rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-indigo-900"
            />
            <span class="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {{ monthBadgeText }}
            </span>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3 lg:min-w-[540px] lg:grid-cols-4">
          <article class="rounded-2xl bg-gray-50 px-4 py-4 dark:bg-gray-900/60">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">结算月份</p>
            <p class="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">{{ selectedMonth }}</p>
          </article>
          <article class="rounded-2xl bg-gray-50 px-4 py-4 dark:bg-gray-900/60">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">待结租户数</p>
            <p class="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">{{ overviewStats.tenantCount }}</p>
          </article>
          <article class="rounded-2xl bg-gray-50 px-4 py-4 dark:bg-gray-900/60">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">待结总金额</p>
            <p class="mt-2 text-lg font-semibold text-amber-600 dark:text-amber-400">{{ formatCurrency(overviewStats.pendingAmount) }}</p>
          </article>
          <article class="rounded-2xl bg-gray-50 px-4 py-4 dark:bg-gray-900/60">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">已结批次数</p>
            <p class="mt-2 text-lg font-semibold text-emerald-600 dark:text-emerald-400">{{ batchPagination.total }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="mb-6">
      <div class="inline-flex rounded-2xl border border-gray-200 bg-white p-1 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="rounded-xl px-4 py-2 text-sm font-medium transition"
          :class="activeTab === tab.id
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>
    </section>

    <section v-if="activeTab === 'overview'" class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex flex-col gap-4 border-b border-gray-100 px-6 py-5 dark:border-gray-700 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">月结总览</h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">按租户查看当前月份的待结金额，并进入单租户月结预览。</p>
        </div>
      </div>

      <div v-if="overviewLoading" class="flex min-h-[280px] items-center justify-center">
        <div class="text-center">
          <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
          <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载月结总览...</p>
        </div>
      </div>

      <div v-else-if="overviewItems.length === 0" class="px-6 py-14 text-center">
        <p class="text-base font-medium text-gray-700 dark:text-gray-200">该月份暂无待结租户</p>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">你可以切换月份查看历史待结情况，或等待新的付费订单进入结算周期。</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">租户</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">待结记录数</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">待结金额</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="item in overviewItems" :key="item.tenantId" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
              <td class="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{{ getTenantName(item.tenantId) }}</td>
              <td class="px-4 py-4 text-right text-sm text-gray-500 dark:text-gray-400">{{ item.pendingCount }}</td>
              <td class="px-4 py-4 text-right text-sm font-medium text-amber-600 dark:text-amber-400">{{ formatCurrency(item.pendingAmount) }}</td>
              <td class="px-4 py-4 text-right text-sm">
                <button
                  type="button"
                  class="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="item.pendingCount === 0 || previewLoading"
                  @click="openPreview(item.tenantId)"
                >
                  {{ selectedMonthIsSettleable ? '预览结算' : '查看明细' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <SettlementBatchHistory
      v-else-if="activeTab === 'batches'"
      :loading="batchesLoading"
      :batches="batchItems"
      :tenants="tenants"
      :selected-tenant-id="batchFilters.tenantId"
      @update:selected-tenant-id="handleBatchTenantChange"
      @view-detail="openBatchDetail"
    />

    <section v-else class="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <p class="text-base font-medium text-gray-700 dark:text-gray-200">原始结算记录</p>
      <p class="mx-auto mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
        当前阶段 Admin 端优先支持“月结总览 + 批次回查”闭环。原始结算记录接口下一阶段接入后，可在这里补充完整审计视图。
      </p>
    </section>

    <Pagination
      v-if="activeTab === 'batches' && batchPagination.total > 0"
      :total="batchPagination.total"
      v-model:current-page="batchPagination.page"
      v-model:page-size="batchPagination.limit"
      @update:current-page="loadBatches"
      @update:page-size="loadBatches"
    />

    <SettlementPreviewDrawer
      :show="previewDrawerOpen"
      :tenant-name="previewTenantName"
      :preview="previewData"
      :payment-reference="confirmForm.paymentReference || ''"
      :note="confirmForm.note || ''"
      :loading="previewLoading"
      :submitting="confirmSubmitting"
      @close="closePreviewDrawer"
      @confirm="confirmSettlement"
      @update:payment-reference="confirmForm.paymentReference = $event"
      @update:note="confirmForm.note = $event"
    />

    <SettlementBatchDetailDrawer
      :show="detailDrawerOpen"
      :tenant-name="detailTenantName"
      :detail="batchDetail"
      :loading="detailLoading"
      @close="closeDetailDrawer"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import Pagination from '@/components/ui/Pagination.vue'
import SettlementBatchDetailDrawer from '@/views/admin/components/tenant-settlement/SettlementBatchDetailDrawer.vue'
import SettlementBatchHistory from '@/views/admin/components/tenant-settlement/SettlementBatchHistory.vue'
import SettlementPreviewDrawer from '@/views/admin/components/tenant-settlement/SettlementPreviewDrawer.vue'
import toaster from '@/utils/toaster'
import { formatCurrency } from '@/utils/formatters'
import {
  confirmTenantSettlementAPI,
  getTenantSettlementBatchDetailAPI,
  getTenantSettlementBatchesAPI,
  getTenantSettlementOverviewAPI,
  getTenantsAPI,
  previewTenantSettlementAPI,
} from '@/api/admin/tenants'
import type {
  AdminTenantConfig,
  ConfirmTenantSettlementRequest,
  TenantSettlementBatch,
  TenantSettlementBatchDetailResponse,
  TenantSettlementOverviewItem,
  TenantSettlementPreviewResponse,
} from '@/types/admin/tenant'

interface BatchPaginationState {
  page: number
  limit: number
  total: number
}

type SettlementTab = 'overview' | 'batches' | 'records'

const tabs: Array<{ id: SettlementTab; label: string }> = [
  { id: 'overview', label: '月结总览' },
  { id: 'batches', label: '结算批次' },
  { id: 'records', label: '原始记录' },
]

const activeTab = ref<SettlementTab>('overview')
const pageLoading = ref(false)
const overviewLoading = ref(false)
const batchesLoading = ref(false)
const previewLoading = ref(false)
const detailLoading = ref(false)
const confirmSubmitting = ref(false)

const tenants = ref<AdminTenantConfig[]>([])
const overviewItems = ref<TenantSettlementOverviewItem[]>([])
const batchItems = ref<TenantSettlementBatch[]>([])
const previewData = ref<TenantSettlementPreviewResponse | null>(null)
const batchDetail = ref<TenantSettlementBatchDetailResponse | null>(null)

const previewDrawerOpen = ref(false)
const detailDrawerOpen = ref(false)
const previewTenantId = ref<number | null>(null)
const detailTenantId = ref<number | null>(null)
const selectedMonth = ref(getPreviousMonth())

const batchPagination = reactive<BatchPaginationState>({
  page: 1,
  limit: 20,
  total: 0,
})

const batchFilters = reactive({
  tenantId: null as number | null,
})

const confirmForm = reactive<ConfirmTenantSettlementRequest>({
  tenantId: 0,
  settlementMonth: '',
  paymentReference: '',
  note: '',
})

/**
 * 获取默认结算月份（上一个自然月）。
 */
function getPreviousMonth(): string {
  const current = new Date()
  current.setMonth(current.getMonth() - 1)
  const year = current.getFullYear()
  const month = `${current.getMonth() + 1}`.padStart(2, '0')
  return `${year}-${month}`
}

/**
 * 获取当前最多允许结算到的月份。
 */
function getAllowedSettlementMonth(): string {
  const current = new Date()
  const currentDay = current.getDate()
  const offset = currentDay >= 8 ? 1 : 2
  current.setMonth(current.getMonth() - offset)
  const year = current.getFullYear()
  const month = `${current.getMonth() + 1}`.padStart(2, '0')
  return `${year}-${month}`
}

const allowedSettlementMonth = computed(() => getAllowedSettlementMonth())

const selectedMonthIsSettleable = computed(() => selectedMonth.value <= allowedSettlementMonth.value)

/**
 * 获取结算月份提示文案。
 */
const monthBadgeText = computed(() => {
  if (selectedMonthIsSettleable.value) {
    return `当前查看 ${selectedMonth.value} 月结数据`
  }
  return `${selectedMonth.value} 仅支持查看明细，不可确认结算`
})

/**
 * 统计总览卡片数据。
 */
const overviewStats = computed(() => {
  const pendingAmount = overviewItems.value.reduce((sum, item) => sum + item.pendingAmount, 0)
  return {
    tenantCount: overviewItems.value.length,
    pendingAmount,
  }
})

/**
 * 获取预览租户名称。
 */
const previewTenantName = computed(() => {
  return getTenantName(previewTenantId.value)
})

/**
 * 获取批次详情租户名称。
 */
const detailTenantName = computed(() => {
  return getTenantName(detailTenantId.value)
})

/**
 * 根据租户 ID 获取租户名称。
 */
function getTenantName(tenantId: number | null): string {
  if (!tenantId) return '未选择租户'
  return tenants.value.find((tenant) => tenant.id === tenantId)?.name || `租户 #${tenantId}`
}

/**
 * 加载租户列表。
 */
async function loadTenants() {
  const response = await getTenantsAPI({ page: 1, limit: 1000 })
  if (response.status === 200 && response.data) {
    tenants.value = response.data.tenants
    return
  }
  toaster.error(response.message || '加载租户列表失败')
}

/**
 * 加载月结总览。
 */
async function loadOverview() {
  overviewLoading.value = true
  try {
    const response = await getTenantSettlementOverviewAPI(selectedMonth.value)
    if (response.status === 200 && response.data) {
      overviewItems.value = response.data.tenants
      return
    }
    // 处理业务规则错误（如月份不符合结算规则）
    if (response.status === 400) {
      overviewItems.value = []
      // 不显示为错误提示，而是静默处理，让空状态组件显示友好提示
      return
    }
    overviewItems.value = []
    toaster.error(response.message || '加载月结总览失败')
  } catch (error) {
    console.error('加载月结总览失败:', error)
    overviewItems.value = []
    toaster.error('加载月结总览失败')
  } finally {
    overviewLoading.value = false
  }
}

/**
 * 加载结算批次列表。
 */
async function loadBatches() {
  batchesLoading.value = true
  try {
    const response = await getTenantSettlementBatchesAPI({
      tenantId: batchFilters.tenantId,
      settlementMonth: selectedMonth.value,
      limit: batchPagination.limit,
      offset: (batchPagination.page - 1) * batchPagination.limit,
    })

    if (response.status === 200 && response.data) {
      batchItems.value = response.data.batches
      batchPagination.total = response.data.total
      return
    }

    batchItems.value = []
    batchPagination.total = 0
    toaster.error(response.message || '加载结算批次失败')
  } catch (error) {
    console.error('加载结算批次失败:', error)
    batchItems.value = []
    batchPagination.total = 0
    toaster.error('加载结算批次失败')
  } finally {
    batchesLoading.value = false
  }
}

/**
 * 打开结算预览。
 */
async function openPreview(tenantId: number) {
  previewDrawerOpen.value = true
  previewLoading.value = true
  previewTenantId.value = tenantId
  previewData.value = null
  confirmForm.tenantId = tenantId
  confirmForm.settlementMonth = selectedMonth.value
  confirmForm.paymentReference = ''
  confirmForm.note = ''

  try {
    const response = await previewTenantSettlementAPI({
      tenantId,
      settlementMonth: selectedMonth.value,
    })

    if (response.status === 200 && response.data) {
      previewData.value = response.data
      return
    }

    toaster.error(response.message || '加载结算预览失败')
    closePreviewDrawer()
  } catch (error) {
    console.error('加载结算预览失败:', error)
    toaster.error('加载结算预览失败')
    closePreviewDrawer()
  } finally {
    previewLoading.value = false
  }
}

/**
 * 关闭结算预览抽屉。
 */
function closePreviewDrawer() {
  previewDrawerOpen.value = false
  previewLoading.value = false
  previewTenantId.value = null
  previewData.value = null
}

/**
 * 确认结算。
 */
async function confirmSettlement() {
  if (!previewData.value) {
    toaster.error('当前没有可确认的结算预览')
    return
  }

  confirmSubmitting.value = true
  try {
    const response = await confirmTenantSettlementAPI({
      tenantId: confirmForm.tenantId,
      settlementMonth: confirmForm.settlementMonth,
      paymentReference: confirmForm.paymentReference?.trim() || undefined,
      note: confirmForm.note?.trim() || undefined,
    })

    if (response.status === 200 && response.data) {
      toaster.success(`月结确认成功，批次 #${response.data.batch.id}`)
      closePreviewDrawer()
      await Promise.all([loadOverview(), loadBatches()])
      return
    }

    toaster.error(response.message || '确认结算失败')
  } catch (error) {
    console.error('确认结算失败:', error)
    toaster.error('确认结算失败')
  } finally {
    confirmSubmitting.value = false
  }
}

/**
 * 打开批次详情。
 */
async function openBatchDetail(batchId: number) {
  detailDrawerOpen.value = true
  detailLoading.value = true
  batchDetail.value = null

  try {
    const response = await getTenantSettlementBatchDetailAPI(batchId)
    if (response.status === 200 && response.data) {
      batchDetail.value = response.data
      detailTenantId.value = response.data.batch.tenantId
      return
    }

    toaster.error(response.message || '加载批次详情失败')
    closeDetailDrawer()
  } catch (error) {
    console.error('加载批次详情失败:', error)
    toaster.error('加载批次详情失败')
    closeDetailDrawer()
  } finally {
    detailLoading.value = false
  }
}

/**
 * 关闭批次详情抽屉。
 */
function closeDetailDrawer() {
  detailDrawerOpen.value = false
  detailLoading.value = false
  batchDetail.value = null
  detailTenantId.value = null
}

/**
 * 处理批次筛选租户切换。
 */
function handleBatchTenantChange(tenantId: number | null) {
  batchFilters.tenantId = tenantId
  batchPagination.page = 1
}

/**
 * 刷新全部数据。
 */
async function refreshAll() {
  pageLoading.value = true
  try {
    await Promise.all([loadTenants(), loadOverview(), loadBatches()])
  } finally {
    pageLoading.value = false
  }
}

watch(selectedMonth, async () => {
  batchPagination.page = 1
  await Promise.all([loadOverview(), loadBatches()])
})

watch(() => batchFilters.tenantId, () => {
  batchPagination.page = 1
  void loadBatches()
})

onMounted(() => {
  void refreshAll()
})
</script>
